import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Input, Modal } from 'antd';
import { ErrorBoundary } from '@sentry/react';
import { MAPBOX_API_KEY } from '../../utils/ClientsideApiKeys';
import { roundCoordinate, toLatitude, toLongitude } from '../../utils/LocationUtils';
import useMountEffect from '../../hooks/useMountEffect';

const mapboxgl = require('mapbox-gl');

/** Street level -- close enough to tell a building entrance from the road centerline. */
const PIN_ZOOM = 17;

const STREETS_STYLE = 'mapbox://styles/mapbox/streets-v11';
/** Satellite makes it obvious which building is which when placing the pin. */
const SATELLITE_STYLE = 'mapbox://styles/mapbox/satellite-streets-v11';

const PIN_COLOR = '#6027b0';
const HINT_LABEL = 'Drag to move';

const MOVED_CLASS_NAME = 'ow-map-pin--moved';

/**
 * The pin, its coaching bubble and the banner all animate, and the pin lives in a Mapbox marker
 * element outside React's tree -- so they are styled here rather than with utility classes.
 */
const MAP_PIN_STYLES = `
@keyframes ow-map-pin-hint-in { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
@keyframes ow-map-pin-hint-nudge { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-3px); } }
@keyframes ow-map-pin-banner-in { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
.ow-map-pin { display: flex; flex-direction: column; align-items: center; gap: 8px; cursor: grab; }
.ow-map-pin:active { cursor: grabbing; }
.ow-map-pin__hint { display: flex; flex-direction: column; align-items: center; animation: ow-map-pin-hint-in .18s ease-out both, ow-map-pin-hint-nudge 2.2s ease-in-out .4s infinite; }
.${MOVED_CLASS_NAME} .ow-map-pin__hint { display: none; }
.ow-map-pin__hint-bubble { display: flex; align-items: center; gap: 6px; padding: 5px 10px; border-radius: 4px; background: rgba(0,0,0,.85); color: #fff; font-size: 13px; line-height: 20px; white-space: nowrap; box-shadow: 0 3px 6px -4px rgba(0,0,0,.24), 0 6px 16px 0 rgba(0,0,0,.08); }
.ow-map-pin__hint-tail { width: 8px; height: 8px; background: rgba(0,0,0,.85); transform: translateY(-4px) rotate(45deg); }
.ow-map-pin__banner { animation: ow-map-pin-banner-in .22s ease-out both; }
`;

const MARKER_ELEMENT_HTML = `
<div class="ow-map-pin__hint">
	<div class="ow-map-pin__hint-bubble">
		<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="opacity:.9">
			<path d="M5 9 L2 12 L5 15"></path><path d="M19 9 L22 12 L19 15"></path><path d="M9 5 L12 2 L15 5"></path><path d="M9 19 L12 22 L15 19"></path><path d="M2 12 H22"></path><path d="M12 2 V22"></path>
		</svg>
		<span>${HINT_LABEL}</span>
	</div>
	<div class="ow-map-pin__hint-tail"></div>
</div>
<svg width="34" height="46" viewBox="0 0 34 46" style="display:block;filter:drop-shadow(0 4px 6px rgba(0,0,0,.28))">
	<path d="M17 1C8.716 1 2 7.716 2 16c0 10.5 15 29 15 29s15-18.5 15-29C32 7.716 25.284 1 17 1z" fill="${PIN_COLOR}" stroke="#fff" stroke-width="1.5"></path>
	<circle cx="17" cy="16" r="5.5" fill="#fff"></circle>
</svg>
`;

const formatCoordinate = (value: number): string => (value === null ? '' : value.toFixed(6));

const formatCoordinatePair = (latitude: number, longitude: number): string =>
	`${formatCoordinate(latitude)}, ${formatCoordinate(longitude)}`;

interface CoordinateMapEditorProps {
	latitude: number;
	longitude: number;
	/** Hides the "drag to move" coaching bubble once the pin has been moved. */
	moved: boolean;
	/** Overlay pinned to the top of the map -- the "address is unchanged" notice. */
	banner?: React.ReactNode;
	onChange: (coordinates: { latitude: number; longitude: number }) => void;
}

const CoordinateMapEditor: React.FC<CoordinateMapEditorProps> = (props) => {
	const { latitude, longitude, moved, banner, onChange } = props;

	const mapContainerRef = useRef<HTMLDivElement>(null);
	const mapRef = useRef<any>(null);
	const markerRef = useRef<any>(null);
	const markerElementRef = useRef<HTMLDivElement>(null);
	const [mapReady, setMapReady] = useState(false);
	const [satelliteView, setSatelliteView] = useState(false);
	const appliedStyleRef = useRef(STREETS_STYLE);

	// The map outlives every render, so its handlers read the latest callback through a ref.
	const onChangeRef = useRef(onChange);
	onChangeRef.current = onChange;

	const emitChange = useCallback((nextLongitude: number, nextLatitude: number) => {
		onChangeRef.current({
			latitude: roundCoordinate(nextLatitude),
			longitude: roundCoordinate(nextLongitude),
		});
	}, []);

	useMountEffect(() => {
		mapboxgl.accessToken = MAPBOX_API_KEY;
		const map = new mapboxgl.Map({
			container: mapContainerRef.current,
			style: STREETS_STYLE,
			center: [longitude, latitude],
			zoom: PIN_ZOOM,
			attributionControl: false,
			// A stray double-click while aiming at a doorway should not zoom the map.
			doubleClickZoom: false,
			dragRotate: false,
			pitchWithRotate: false,
			touchPitch: false,
		});
		mapRef.current = map;

		const markerElement = document.createElement('div');
		markerElement.className = 'ow-map-pin';
		markerElement.innerHTML = MARKER_ELEMENT_HTML;
		markerElementRef.current = markerElement;

		const marker = new mapboxgl.Marker({
			element: markerElement,
			anchor: 'bottom',
			draggable: true,
		});
		marker.setLngLat([longitude, latitude]).addTo(map);
		marker.on('dragend', () => {
			const lngLat = marker.getLngLat();
			emitChange(lngLat.lng, lngLat.lat);
		});
		markerRef.current = marker;

		map.on('load', () => {
			// The modal is still animating open when the map is built, so it measures the wrong size.
			map.resize();
			setMapReady(true);
		});

		map.on('click', (event) => emitChange(event.lngLat.lng, event.lngLat.lat));
		map.getCanvas().style.cursor = 'crosshair';

		return () => {
			marker.remove();
			markerRef.current = null;
			map.remove();
			mapRef.current = null;
		};
	});

	// Typing a coordinate, or resetting the pin, moves it from outside the map.
	useEffect(() => {
		const map = mapRef.current;
		if (!map || !markerRef.current) {
			return;
		}
		markerRef.current.setLngLat([longitude, latitude]);
		if (!map.getBounds().contains([longitude, latitude])) {
			map.easeTo({ center: [longitude, latitude], duration: 400 });
		}
	}, [latitude, longitude]);

	useEffect(() => {
		const markerElement = markerElementRef.current;
		if (!markerElement) {
			return;
		}
		if (moved) {
			markerElement.classList.add(MOVED_CLASS_NAME);
		} else {
			markerElement.classList.remove(MOVED_CLASS_NAME);
		}
	}, [moved]);

	useEffect(() => {
		const map = mapRef.current;
		if (!map || !mapReady) {
			return;
		}
		const nextStyle = satelliteView ? SATELLITE_STYLE : STREETS_STYLE;
		if (nextStyle === appliedStyleRef.current) {
			return;
		}
		appliedStyleRef.current = nextStyle;
		map.setStyle(nextStyle);
	}, [satelliteView, mapReady]);

	const handleZoomIn = useCallback(() => {
		if (mapRef.current) {
			mapRef.current.zoomIn();
		}
	}, []);

	const handleZoomOut = useCallback(() => {
		if (mapRef.current) {
			mapRef.current.zoomOut();
		}
	}, []);

	const handleToggleSatellite = useCallback(() => {
		setSatelliteView((previous) => !previous);
	}, []);

	const mapControlClassName =
		'box-border flex h-8 w-8 cursor-pointer items-center justify-center rounded-sm border border-solid border-grey-5 bg-white p-0 text-[16px] leading-none text-character-secondary hover:border-purple-6 hover:text-purple-6';

	return (
		<div className="relative h-full min-h-[280px] w-full overflow-hidden rounded border border-solid border-grey-5 bg-grey-3">
			<div className="absolute inset-0 h-full w-full" ref={mapContainerRef} />
			{banner ? (
				<div className="ow-map-pin__banner absolute inset-x-3 top-3 z-[4]">{banner}</div>
			) : null}
			<button
				type="button"
				className={
					'bodyregular absolute bottom-3 left-3 z-[2] box-border flex h-8 cursor-pointer items-center rounded-sm border border-solid border-grey-5 px-3 hover:border-purple-6 ' +
					(satelliteView ? 'bg-purple-1 text-purple-7' : 'bg-white text-character-secondary')
				}
				onClick={handleToggleSatellite}
			>
				Satellite
			</button>
			<div className="absolute bottom-3 right-3 z-[2] flex flex-col gap-2xs">
				<button
					type="button"
					className={mapControlClassName}
					onClick={handleZoomIn}
					title="Zoom in"
				>
					+
				</button>
				<button
					type="button"
					className={mapControlClassName}
					onClick={handleZoomOut}
					title="Zoom out"
				>
					−
				</button>
			</div>
		</div>
	);
};

interface LocationMapPinProps {
	/** Latitude/longitude currently held by the form. */
	latitude?: number | string;
	longitude?: number | string;
	/** Coordinates the address geocoded to -- where "Reset Pin Location" puts the pin back. */
	addressLatitude?: number | string;
	addressLongitude?: number | string;
	/** Address shown beside the modal title, e.g. "123 William Street, Unit 4". */
	addressLabel?: string;
	disabled?: boolean;
	onChange: (coordinates: { latitude: number; longitude: number }) => void;
}

const LocationMapPinComponent: React.FC<LocationMapPinProps> = (props) => {
	const {
		latitude,
		longitude,
		addressLatitude,
		addressLongitude,
		addressLabel,
		disabled,
		onChange,
	} = props;

	const savedLatitude = toLatitude(latitude);
	const savedLongitude = toLongitude(longitude);
	const baselineLatitude = toLatitude(addressLatitude);
	const baselineLongitude = toLongitude(addressLongitude);
	const hasCoordinates = savedLatitude !== null && savedLongitude !== null;
	const hasBaseline = baselineLatitude !== null && baselineLongitude !== null;

	const [modalOpen, setModalOpen] = useState(false);
	/**
	 * The modal edits a draft, and nothing reaches the form until Save. The draft is an *override*
	 * rather than a snapshot: null means "whatever the form currently holds", so an untouched modal
	 * follows a geocode that lands after it opened -- clicking EDIT COORDINATES is itself what blurs
	 * the address input, so that geocode is usually still in flight. Once the user drags, types or
	 * resets, the override holds and their edit wins.
	 */
	const [draftOverride, setDraftOverride] = useState(null);
	// Null means "show the draft's own value"; a string is what the user is part-way through typing.
	const [latitudeText, setLatitudeText] = useState(null);
	const [longitudeText, setLongitudeText] = useState(null);
	// Drives the coaching bubble and the notice: true once the pin has been moved this session.
	const [moved, setMoved] = useState(false);

	const draftLatitude = draftOverride ? draftOverride.latitude : savedLatitude;
	const draftLongitude = draftOverride ? draftOverride.longitude : savedLongitude;

	const overrideDraft = useCallback((nextLatitude: number, nextLongitude: number) => {
		setDraftOverride({ latitude: nextLatitude, longitude: nextLongitude });
		setLatitudeText(null);
		setLongitudeText(null);
	}, []);

	const handleOpen = useCallback(() => {
		setDraftOverride(null);
		setLatitudeText(null);
		setLongitudeText(null);
		setMoved(false);
		setModalOpen(true);
	}, []);

	const handleCancel = useCallback(() => {
		setModalOpen(false);
		setMoved(false);
	}, []);

	const handleSave = useCallback(() => {
		setModalOpen(false);
		setMoved(false);
		onChange({ latitude: draftLatitude, longitude: draftLongitude });
	}, [draftLatitude, draftLongitude, onChange]);

	const handlePinChange = useCallback(
		({ latitude: nextLatitude, longitude: nextLongitude }) => {
			overrideDraft(nextLatitude, nextLongitude);
			setMoved(true);
		},
		[overrideDraft]
	);

	const handleResetPin = useCallback(() => {
		if (!hasBaseline) {
			return;
		}
		overrideDraft(baselineLatitude, baselineLongitude);
		setMoved(false);
	}, [overrideDraft, baselineLatitude, baselineLongitude, hasBaseline]);

	const handleLatitudeText = useCallback(
		(event) => {
			const text = event.target.value;
			setLatitudeText(text);
			const nextLatitude = toLatitude(text);
			if (nextLatitude === null) {
				return;
			}
			setDraftOverride({ latitude: nextLatitude, longitude: draftLongitude });
			setMoved(true);
		},
		[draftLongitude]
	);

	const handleLongitudeText = useCallback(
		(event) => {
			const text = event.target.value;
			setLongitudeText(text);
			const nextLongitude = toLongitude(text);
			if (nextLongitude === null) {
				return;
			}
			setDraftOverride({ latitude: draftLatitude, longitude: nextLongitude });
			setMoved(true);
		},
		[draftLatitude]
	);

	// Nothing to place a pin against until the address has produced coordinates.
	if (!hasCoordinates) {
		return null;
	}

	// A null text field is not being edited, so it shows the draft's own value and is always valid.
	const latitudeValue = latitudeText === null ? formatCoordinate(draftLatitude) : latitudeText;
	const longitudeValue = longitudeText === null ? formatCoordinate(draftLongitude) : longitudeText;
	const latitudeInvalid = latitudeText !== null && toLatitude(latitudeText) === null;
	const longitudeInvalid = longitudeText !== null && toLongitude(longitudeText) === null;
	const saveDisabled = latitudeInvalid || longitudeInvalid;

	return (
		<div>
			<style>{MAP_PIN_STYLES}</style>
			<div className="flex flex-row flex-wrap items-center gap-x-s gap-y-2xs">
				{!disabled ? (
					<Button type="link" className="bodymedium px-0 tracking-[.02em]" onClick={handleOpen}>
						EDIT COORDINATES
					</Button>
				) : null}
				<div className="flex flex-row items-baseline gap-x-xs">
					<span className="footnoteregular text-grey-7">Coordinates</span>
					<span className="footnoteregular whitespace-nowrap font-mono text-character-secondary">
						{formatCoordinatePair(savedLatitude, savedLongitude)}
					</span>
				</div>
			</div>
			<Modal
				visible={modalOpen}
				width={880}
				centered
				destroyOnClose
				maskClosable={false}
				onCancel={handleCancel}
				title={
					<div className="flex flex-row flex-wrap items-baseline gap-x-2xs">
						<span className="h5medium text-character-primary">Set Exact Coordinates</span>
						<span className="bodyregular text-character-secondary">
							{addressLabel || 'This address'}
						</span>
					</div>
				}
				footer={
					<div className="flex flex-row justify-end gap-x-xs">
						<Button className="bodymedium tracking-[.02em]" onClick={handleCancel}>
							CANCEL
						</Button>
						<Button
							type="primary"
							className="bodymedium tracking-[.02em]"
							disabled={saveDisabled}
							onClick={handleSave}
						>
							SAVE
						</Button>
					</div>
				}
			>
				<div className="flex flex-col gap-m">
					<div className="flex flex-row flex-wrap items-end gap-s">
						<div className="flex flex-col gap-2xs">
							<span className="footnoteregular text-character-secondary">Latitude</span>
							<Input
								value={latitudeValue}
								onChange={handleLatitudeText}
								status={latitudeInvalid ? 'error' : null}
								placeholder="40.709250"
								style={{ width: 220 }}
							/>
						</div>
						<div className="flex flex-col gap-2xs">
							<span className="footnoteregular text-character-secondary">Longitude</span>
							<Input
								value={longitudeValue}
								onChange={handleLongitudeText}
								status={longitudeInvalid ? 'error' : null}
								placeholder="-74.006950"
								style={{ width: 220 }}
							/>
						</div>
						<span className="footnoteregular pb-xxs text-grey-7">
							Type coordinates, or drag the pin on the map.
						</span>
					</div>
					<div className="h-[380px]">
						<CoordinateMapEditor
							latitude={draftLatitude}
							longitude={draftLongitude}
							moved={moved}
							onChange={handlePinChange}
							banner={
								moved ? (
									<Alert
										type="info"
										showIcon
										message="Dragging the pin updates the exact coordinates, but won't change your saved street address."
										action={
											hasBaseline ? (
												<Button type="link" size="small" onClick={handleResetPin}>
													Reset Pin Location
												</Button>
											) : null
										}
									/>
								) : null
							}
						/>
					</div>
				</div>
			</Modal>
		</div>
	);
};

const LocationMapPin: React.FC<LocationMapPinProps> = (props) => (
	<ErrorBoundary fallback={null}>
		<LocationMapPinComponent {...props} />
	</ErrorBoundary>
);

export { LocationMapPin };
export default LocationMapPin;
